import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { StarRating } from "@/components/store/ProductCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Reviews() {
  const [data, setData] = useState({ reviews: [], total: 0, average: 0, breakdown: {} });
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const params = new URLSearchParams();
    if (["1", "2", "3", "4", "5"].includes(filter)) params.set("rating", filter);
    if (filter === "photos") params.set("with_photos", "true");
    if (filter === "verified") params.set("verified", "true");
    try { const r = await api.get(`/reviews?${params}`); setData(r.data); } catch {}
  };
  useEffect(() => { load(); }, [filter]);

  const FILTERS = [["all", "All"], ["5", "5 Star"], ["4", "4 Star"], ["3", "3 Star"], ["2", "2 Star"], ["1", "1 Star"], ["photos", "With Photos"], ["verified", "Verified"]];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-14">
      <div className="text-center mb-10">
        <h1 className="font-serif text-4xl sm:text-5xl">WHAT SHE'S SAYING ♡</h1>
        <p className="text-sharoa-muted mt-2">Real women. Real styles. Real SHAROA.</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-6 items-center bg-sharoa-ivory p-6 mb-8">
        <div className="text-center">
          <p className="font-serif text-5xl text-sharoa-rose">{data.average}</p>
          <StarRating value={data.average} size={16} />
          <p className="text-xs text-sharoa-muted mt-1">{data.total} reviews</p>
        </div>
        <div className="sm:col-span-2 space-y-1">
          {[5, 4, 3, 2, 1].map((s) => {
            const c = data.breakdown?.[s] || 0;
            const pct = data.total ? (c / data.total) * 100 : 0;
            return (
              <div key={s} className="flex items-center gap-2 text-xs">
                <span className="w-8">{s}★</span>
                <div className="flex-1 h-2 bg-white rounded-full overflow-hidden"><div className="h-full bg-sharoa-rose" style={{ width: `${pct}%` }} /></div>
                <span className="w-8 text-right text-sharoa-muted">{c}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} className={`text-xs uppercase tracking-widest px-3 py-2 border ${filter === v ? "bg-sharoa-charcoal text-white border-sharoa-charcoal" : "border-sharoa-line"}`}>{l}</button>
          ))}
        </div>
        <WriteReview onDone={load} />
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        {data.reviews.map((r) => (
          <div key={r.id} className="border border-sharoa-line p-5">
            <div className="flex justify-between items-start">
              <StarRating value={r.rating} />
              {r.featured && <span className="text-[10px] uppercase tracking-widest text-sharoa-rose">Featured</span>}
            </div>
            <h4 className="font-serif text-lg mt-2">{r.title}</h4>
            <p className="text-sm text-sharoa-muted mt-1">{r.body}</p>
            {r.photos?.length > 0 && <div className="flex gap-2 mt-3">{r.photos.map((p, i) => <img key={i} src={p} alt="" className="w-16 h-16 object-cover" />)}</div>}
            <p className="text-xs text-sharoa-charcoal mt-3">{r.name} · <span className="text-sharoa-muted">{r.product_name}</span> {r.verified && <span className="text-sharoa-rose">· ✓ Verified Buyer</span>}</p>
            <p className="text-[10px] text-sharoa-muted mt-1">{new Date(r.created_at).toLocaleDateString("en-IN")}</p>
          </div>
        ))}
        {data.reviews.length === 0 && <p className="text-sharoa-muted col-span-2 text-center py-10">No reviews for this filter yet.</p>}
      </div>
    </div>
  );
}

function WriteReview({ onDone }) {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [f, setF] = useState({ name: "", email: "", order_number: "", product_slug: "", rating: 5, title: "", body: "", size_purchased: "", fit: "" });
  useEffect(() => { if (open) api.get("/products?limit=200").then((r) => setProducts(r.data)).catch(() => {}); }, [open]);

  const submit = async () => {
    if (!f.product_slug || !f.title || !f.body) { toast.error("Please fill product, title and review"); return; }
    try { const r = await api.post("/reviews", f); toast(r.data.message); setOpen(false); onDone?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="write-review-btn" className="bg-sharoa-rose text-white text-xs uppercase tracking-widest px-5 py-2.5">Write a Review</button></DialogTrigger>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Write a Review</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <F ph="Name" v={f.name} on={(x) => setF({ ...f, name: x })} />
            <F ph="Email" v={f.email} on={(x) => setF({ ...f, email: x })} />
          </div>
          <F ph="Order Number (for Verified Buyer)" v={f.order_number} on={(x) => setF({ ...f, order_number: x })} />
          <select data-testid="review-product" value={f.product_slug} onChange={(e) => setF({ ...f, product_slug: e.target.value })} className="w-full border border-sharoa-line px-3 py-2.5 text-sm">
            <option value="">Select Product</option>
            {products.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
          </select>
          <div className="flex gap-1">{[1, 2, 3, 4, 5].map((i) => <button key={i} type="button" onClick={() => setF({ ...f, rating: i })} className="text-2xl text-sharoa-rose">{i <= f.rating ? "★" : "☆"}</button>)}</div>
          <F ph="Title" v={f.title} on={(x) => setF({ ...f, title: x })} />
          <textarea placeholder="Your review" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="w-full border border-sharoa-line px-3 py-2.5 text-sm h-24" />
          <div className="grid grid-cols-2 gap-3">
            <F ph="Size Purchased" v={f.size_purchased} on={(x) => setF({ ...f, size_purchased: x })} />
            <F ph="Fit (True to size…)" v={f.fit} on={(x) => setF({ ...f, fit: x })} />
          </div>
          <button data-testid="review-submit" onClick={submit} className="w-full bg-sharoa-rose text-white text-xs uppercase tracking-widest py-3">Submit Review</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
const F = ({ ph, v, on }) => <input placeholder={ph} value={v} onChange={(e) => on(e.target.value)} className="w-full border border-sharoa-line px-3 py-2.5 text-sm outline-none focus:border-sharoa-rose" />;
